import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useMirrorTargetsQuery } from "../../generated/graphql";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import MirrorCapturePanel from "./components/mirror-capture-panel";
import MirrorSyncPanel from "./components/mirror-sync-panel";
import MirrorTargetsPanel from "./components/mirror-targets-panel";
import OutboxMaintenancePanel from "./components/outbox-maintenance-panel";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 3,
  } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    p: 6,
  } satisfies SxProps<Theme>,
};

/**
 * Internal admin page for the order-mirror feature.
 *
 * The backend exposes two mutually exclusive surfaces: production manages
 * mirror targets and per-company gating, while non-production environments
 * request syncs of historical production orders. Rather than relying on a
 * build-time environment flag, this page probes the `mirrorTargets` query:
 * production answers it, non-production rejects it with "only available on
 * production" — which selects the sync panel instead. The split is enforced
 * server-side either way.
 *
 * Access requires the InternalTools write (or Master) permission, mirroring
 * the backend's `@InternalTools()` guard.
 */
const OrderMirrorPage = () => {
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();
  const {
    data,
    loading: probeLoading,
    error,
  } = useMirrorTargetsQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: permissionsLoading || !canAccess,
  });

  if (permissionsLoading || probeLoading) {
    return (
      <Box sx={styles.centered}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canAccess) {
    return (
      <Box sx={styles.page}>
        <Alert severity="error">
          You do not have access to internal tools.
        </Alert>
      </Box>
    );
  }

  const isProducerEnvironment = data?.mirrorTargets !== undefined;
  const isConsumerEnvironment =
    error?.message.includes("only available on production") ?? false;

  return (
    <Box sx={styles.page}>
      <Typography variant="h4">Order mirroring</Typography>
      {isProducerEnvironment && <MirrorCapturePanel />}
      {isProducerEnvironment && <MirrorTargetsPanel />}
      {!isProducerEnvironment && isConsumerEnvironment && <MirrorSyncPanel />}
      {(isProducerEnvironment || isConsumerEnvironment) && (
        <OutboxMaintenancePanel />
      )}
      {!isProducerEnvironment && !isConsumerEnvironment && (
        <Alert severity="error">
          {error?.message ?? "Unable to determine the mirroring mode."}
        </Alert>
      )}
    </Box>
  );
};

export default OrderMirrorPage;
